"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileNameWithAiMarker from "@/components/common/profile/ProfileNameWithAiMarker";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import WaveDropTime from "./time/WaveDropTime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { DropAuthorBadges } from "./DropAuthorBadges";
import { useCallback } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useCompactMode } from "@/contexts/CompactModeContext";
interface WaveDropHeaderProps {
  readonly drop: ApiDrop;
  readonly isStorm: boolean;
  readonly currentPartIndex: number;
  readonly partsCount: number;
  readonly showWaveInfo: boolean;
  readonly badge?: React.ReactNode | undefined;
  readonly showActionsButton?: boolean | undefined;
  readonly onOpenActions?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
}
const WaveDropHeader: React.FC<WaveDropHeaderProps> = t0 => {
  const $ = _c(56);
  const {
    drop,
    isStorm,
    currentPartIndex,
    partsCount,
    showWaveInfo,
    badge,
    showActionsButton: t1,
    onOpenActions
  } = t0;
  const showActionsButton = t1 === undefined ? false : t1;
  const router = useRouter();
  const compact = useCompactMode();
  const authorIdentity = drop.author.handle ?? drop.author.primary_address;
  let t2;
  if ($[0] !== router) {
    t2 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[0] = router;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleNavigation = t2;
  const t3 = `tw-mb-0 tw-font-semibold tw-leading-none ${compact ? "tw-text-sm" : "tw-text-md"}`;
  let t4;
  if ($[2] !== authorIdentity || $[3] !== handleNavigation) {
    t4 = e_0 => handleNavigation(e_0, `/${authorIdentity}`);
    $[2] = authorIdentity;
    $[3] = handleNavigation;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = `/${authorIdentity}`;
  let t6;
  if ($[5] !== authorIdentity || $[6] !== drop.author.classification) {
    t6 = <ProfileNameWithAiMarker classification={drop.author.classification}>{authorIdentity}</ProfileNameWithAiMarker>;
    $[5] = authorIdentity;
    $[6] = drop.author.classification;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = <Link onClick={t4} href={t5} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-opacity-80 desktop-hover:hover:tw-underline">{t6}</Link>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== authorIdentity || $[13] !== t7) {
    t8 = <UserProfileTooltipWrapper user={authorIdentity}>{t7}</UserProfileTooltipWrapper>;
    $[12] = authorIdentity;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t3 || $[16] !== t8) {
    t9 = <p className={t3}>{t8}</p>;
    $[15] = t3;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== drop.author.level) {
    t10 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[18] = drop.author.level;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = `header-author-badges-${drop.id}`;
  let t12;
  if ($[20] !== drop.author || $[21] !== t11) {
    t12 = <DropAuthorBadges profile={drop.author} tooltipIdPrefix={t11} />;
    $[20] = drop.author;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== drop.created_at) {
    t14 = <WaveDropTime timestamp={drop.created_at} />;
    $[24] = drop.created_at;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t10 || $[27] !== t12 || $[28] !== t14 || $[29] !== t9) {
    t15 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1">{t9}{t10}{t12}{t13}{t14}</div>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t14;
    $[29] = t9;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== badge) {
    t16 = !!badge && <div className="tw-ml-2">{badge}</div>;
    $[31] = badge;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t15 || $[34] !== t16) {
    t17 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1">{t15}{t16}</div>;
    $[33] = t15;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== onOpenActions || $[37] !== showActionsButton) {
    t18 = showActionsButton && onOpenActions && <button type="button" aria-label="Open drop actions" onClick={onOpenActions} className="tw-hidden tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-1.5 tw-text-iron-300 active:tw-bg-iron-800 desktop-hover:hover:tw-bg-iron-900 desktop-hover:hover:tw-text-iron-100 md:tw-flex"><EllipsisVerticalIcon className="tw-h-5 tw-w-5" /></button>;
    $[36] = onOpenActions;
    $[37] = showActionsButton;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t17 || $[40] !== t18) {
    t19 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-2">{t17}{t18}</div>;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== drop.wave || $[43] !== handleNavigation || $[44] !== showWaveInfo) {
    t20 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage: waveMeta?.scope?.group?.is_direct_message ?? false,
        isApp: false
      });
      return <Link onClick={e_1 => handleNavigation(e_1, waveHref)} href={waveHref} className="tw-leading-0 -tw-mt-1 tw-mb-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[42] = drop.wave;
    $[43] = handleNavigation;
    $[44] = showWaveInfo;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t20) {
    t21 = <div>{t20}</div>;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== currentPartIndex || $[49] !== isStorm || $[50] !== partsCount) {
    t22 = isStorm && <div className="tw-relative tw-mt-2 tw-inline-flex"><span className="tw-mb-1.5 tw-text-xs tw-text-iron-50">{currentPartIndex + 1} /{" "}<span className="tw-text-iron-400">{partsCount}</span></span></div>;
    $[48] = currentPartIndex;
    $[49] = isStorm;
    $[50] = partsCount;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t19 || $[53] !== t21 || $[54] !== t22) {
    t23 = <>{t19}{t21}{t22}</>;
    $[52] = t19;
    $[53] = t21;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  return t23;
};
export default WaveDropHeader;